import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, resetClientSecurityState, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { clearPrivateCaches, queryClient } from './queryClient';

export type CurrentUser = components['schemas']['CurrentUser'];
export type MembershipSummary = components['schemas']['MembershipSummary'];
export type Role = MembershipSummary['role'];
export type Scope = MembershipSummary['scopes'][number];

export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
}

export interface Membership {
  membershipId: string;
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: Role;
  actorId: string;
  scopes: MembershipSummary['scopes'];
}

function toMembership(m: MembershipSummary): Membership {
  return {
    membershipId: m.membership_id,
    orgId: m.org_id,
    orgName: m.org_name,
    orgSlug: m.org_slug,
    role: m.role,
    actorId: m.actor_id,
    scopes: m.scopes,
  };
}

/** getCurrentUser: user, current session and memberships in one call. */
export const meQuery = queryOptions({
  queryKey: ['session', 'me'],
  queryFn: ({ signal }) => unwrap(api.GET('/api/v1/me', { signal })),
  staleTime: 60_000,
  retry: false,
});

interface OrgContextValue {
  user: SessionUser | undefined;
  memberships: Membership[];
  org: Membership | undefined;
  switchOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);
const STORAGE_KEY = 'eryce.org';

export function OrgProvider({ children }: { children: ReactNode }) {
  const me = useQuery(meQuery);
  const [chosen, setChosen] = useState<string | null>(() => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const memberships = useMemo(
    () =>
      (me.data?.memberships ?? [])
        .filter((m) => m.status === 'active' && m.org_status === 'active')
        .map(toMembership),
    [me.data],
  );
  const user = useMemo<SessionUser | undefined>(
    () => (me.data ? { id: me.data.user.id, username: me.data.user.username, displayName: me.data.user.display_name } : undefined),
    [me.data],
  );
  const org = memberships.find((m) => m.orgId === chosen) ?? memberships[0];

  const switchOrg = useCallback((orgId: string) => {
    // Never show previous tenant data while the next tenant loads.
    clearPrivateCaches();
    setChosen(orgId);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      /* tab scoped preference only */
    }
  }, []);

  const value = useMemo(() => ({ user, memberships, org, switchOrg }), [user, memberships, org, switchOrg]);
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrgContext outside OrgProvider');
  return ctx;
}

/** Current tenant. Private pages only render once a membership is resolved. */
export function useOrg(): Membership & { isAdmin: boolean; hasScope: (scope: Scope) => boolean } {
  const { org } = useOrgContext();
  if (!org) throw new Error('useOrg rendered without a resolved organization');
  const scopes = org.scopes;
  return { ...org, isAdmin: org.role === 'admin', hasScope: (scope) => scopes.includes(scope) };
}

export function useOrgId(): string {
  return useOrg().orgId;
}

export async function signOut(): Promise<void> {
  try {
    await unwrap(api.POST('/api/v1/auth/logout'));
  } finally {
    clearPrivateCaches();
    queryClient.removeQueries({ queryKey: ['session'] });
    resetClientSecurityState();
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}
